import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { useMyTenants } from '@/hooks/useTenants'
import { getErrorStatus } from '@/api/errors'
import { clearSelectedTenantId, getSelectedTenantId, setSelectedTenantId } from '@/lib/tenantStorage'


export interface Tenant {
  id: string
  name: string
  cloudProvider: 'Azure' | 'Aws' | 'OnPremise'
  status: 'Active' | 'Suspended' | 'Expired'
  role: string
  logoUrl?: string
}

interface TenantContextValue {
  currentTenant: Tenant | null
  availableTenants: Tenant[]
  setCurrentTenant: (tenant: Tenant) => void
  clearTenant: () => void
  isResolved: boolean
  isLoading: boolean
  isError: boolean
  refetchTenants: () => void
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined)

export function TenantProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const { data: apiTenants, isLoading, isError, error, refetch } = useMyTenants({ enabled: isAuthenticated })

  const availableTenants: Tenant[] = (apiTenants ?? []).map(t => ({
    id: t.id,
    name: t.name,
    cloudProvider: t.cloudProvider as 'Azure' | 'Aws' | 'OnPremise',
    status: t.status as 'Active' | 'Suspended' | 'Expired',
    role: t.role,
  }))

  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)

  const setCurrentTenant = useCallback((tenant: Tenant) => {
    setCurrentTenantState(tenant)
    setSelectedTenantId(tenant.id)
  }, [])

  const clearTenant = useCallback(() => {
    setCurrentTenantState(null)
    clearSelectedTenantId()
  }, [])

  // Restore the tenant selected in this tab, falling back to the last one selected in any tab.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return
    // A suspended tenant in the stored id makes the backend reject even /api/tenants/mine, which
    // would otherwise leave every tab stranded with no way to reach the user's other tenants.
    if (isError) {
      if (getErrorStatus(error) === 403 && getSelectedTenantId()) {
        clearTenant()
        void refetch()
      }
      setHasInitialized(true)
      return
    }
    if (availableTenants.length > 0) {
      const stored = getSelectedTenantId()
      if (stored) {
        const found = availableTenants.find(t => t.id === stored)
        if (found) {
          setCurrentTenantState(found)
          setHasInitialized(true)
          return
        }
        clearTenant()
      }
      // Auto-select if user has only 1 tenant
      if (availableTenants.length === 1 && availableTenants[0].status === 'Active') {
        setCurrentTenantState(availableTenants[0])
        setSelectedTenantId(availableTenants[0].id)
      }
    }
    setHasInitialized(true)
  }, [apiTenants, isLoading, isAuthenticated, isError, error, refetch, clearTenant])

  return (
    <TenantContext.Provider value={{
      currentTenant,
      availableTenants,
      setCurrentTenant,
      clearTenant,
      isResolved: currentTenant !== null,
      isLoading: isLoading || !hasInitialized,
      isError,
      refetchTenants: () => { refetch() },
    }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
